/**
 * "Propostas": pending extraction proposals, grouped by the dimension they extend. Approving,
 * merging and rejecting all resolve one proposal at a time -- there is no bulk action in the
 * contract, so this queue offers none either. Approving takes one click: the proposal already
 * names its dimension, and the server places the value under the suggested parent or at the
 * root, where the tree can still move it. Nothing renders while no proposal is pending.
 */
import { useState, type ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { integer, percent } from '../../../i18n/format';
import { Card, CardContent } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { useAcceptProposal, useMergeProposal, useOntology, useRejectProposal } from './api/queries';
import type { OntologyDimension, OntologyProposal, OntologyScope } from './api/schemas';
import { om, omPlural } from './messages';
import { ValuePicker } from './ValuePicker';

function ProposalRow(props: {
  tenantId: string;
  scope: OntologyScope;
  dimension: OntologyDimension;
  proposal: OntologyProposal;
}): ReactNode {
  const { tenantId, scope, dimension, proposal } = props;
  const [merging, setMerging] = useState(false);
  const locale = getLocale();
  const acceptMutation = useAcceptProposal(tenantId, scope);
  const mergeMutation = useMergeProposal(tenantId, scope);
  const rejectMutation = useRejectProposal(tenantId, scope);

  return (
    <div className="flex flex-col gap-2 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body-sm font-medium text-foreground">{proposal.name}</span>
        {proposal.aliases.map((alias) => (
          <Chip key={alias} size="sm">
            {alias}
          </Chip>
        ))}
      </div>
      <div className="flex items-center gap-3 text-caption text-muted-foreground">
        <span>
          {omPlural('proposals.occurrences.other', proposal.occurrences, {
            n: integer(proposal.occurrences, locale),
          })}
        </span>
        <span>{om('proposals.confidence', { value: percent(proposal.confidence, locale) })}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={acceptMutation.isPending}
          onClick={() => acceptMutation.mutate({ proposal, dimensionName: dimension.name })}
        >
          {om('proposals.approve')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setMerging(true)}>
          {om('proposals.mergeInto')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={rejectMutation.isPending}
          onClick={() => rejectMutation.mutate(proposal.id)}
        >
          {om('proposals.reject')}
        </Button>
      </div>

      <Dialog open={merging} onOpenChange={setMerging}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{om('proposals.merge.title', { name: proposal.name })}</DialogTitle>
          </DialogHeader>
          <ValuePicker
            values={dimension.values}
            exclude={new Set()}
            onSelect={(into) => {
              if (into === null) return;
              mergeMutation.mutate({ proposalId: proposal.id, into });
              setMerging(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ProposalsQueue(props: {
  tenantId: string;
  scope: OntologyScope;
  dimensions: OntologyDimension[];
  proposals: OntologyProposal[];
}): ReactNode {
  const { tenantId, scope, dimensions, proposals } = props;
  const dimensionsById = new Map(dimensions.map((dimension) => [dimension.id, dimension]));

  const groups = dimensions
    .map((dimension) => ({
      dimension,
      items: proposals.filter((proposal) => proposal.dimension_id === dimension.id),
    }))
    .filter((group) => group.items.length > 0);

  if (groups.length === 0) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <h2 className="text-title-sm text-foreground">{om('proposals.heading')}</h2>
        {groups.map(({ dimension, items }) => (
          <div key={dimension.id} className="flex flex-col">
            <h3 className="text-eyebrow text-muted-foreground">{dimension.name}</h3>
            {items.map((proposal) => (
              <ProposalRow
                key={proposal.id}
                tenantId={tenantId}
                scope={scope}
                dimension={dimensionsById.get(proposal.dimension_id) ?? dimension}
                proposal={proposal}
              />
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** The sidebar's pending count for one scope; nothing while none is pending or the read failed. */
export function PendingProposalsBadge(props: {
  scope: OntologyScope;
  params?: Record<string, string>;
}): ReactNode {
  const tenantId = props.params?.tenantId ?? '';
  const count = useOntology(tenantId, props.scope).data?.proposals.length ?? 0;
  if (count === 0) return null;
  return (
    <span className="ml-auto rounded-sm bg-secondary px-sm py-2xs text-caption text-muted-foreground">
      <span aria-hidden>{count}</span>
      <span className="sr-only">
        {omPlural('nav.proposals.pending.other', count, { n: count })}
      </span>
    </span>
  );
}
